import database from 'src/providers/watermelondb';
import moment from 'moment';
import _ from 'lodash';
import {Q} from '@nozbe/watermelondb';

export const exportFrequency = async ({
  sets = [],
  events = [],
  tableEvents = [],
  session = undefined,
  startDate = undefined,
  endDate = undefined,
}: {
  sets: any[];
  events?: any[]; // observables-style (event.set.id)
  tableEvents?: any[]; // HTTP-style (event.set_id)
  session?: any;
  startDate?: string | number | Date;
  endDate?: string | number | Date;
}) => {
  const rows: any[] = [
    [
      'Date',
      'Time Stamp',
      'Collector',
      'Number of Instances',
      'Antecedents',
      'Consequences',
      'Mild',
      'Moderate',
      'Severe',
    ],
  ];

  const val = (v: any, alt: any) => (v !== undefined && v !== null ? v : alt);
  const setStart = (s: any) =>
    val(s?.startTimestamp, s?.start_timestamp ?? s?.start);
  const setEnd = (s: any) => val(s?.endTimestamp, s?.end_timestamp ?? s?.end);

  const normalizePromptsArray = (ps: any): any[] => {
    if (Array.isArray(ps)) {
      return ps;
    }
    if (ps == null) {
      return [];
    }
    if (typeof ps === 'string') {
      try {
        const parsed = JSON.parse(ps);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        if (ps.includes(',')) {
          return ps
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean);
        }
        return [ps]; // single id string
      }
    }
    return [];
  };

  const filteredSets =
    startDate && endDate
      ? sets.filter((s: any) => {
          const ts = Number(setStart(s)) || 0;
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return ts >= start.getTime() && ts <= end.getTime();
        })
      : sets;

  // ---- Auto-pick the source & auto-detect the event shape ----
  const allEvents = events?.length ? events : tableEvents || [];
  const eventsBySetId = _.groupBy(allEvents, (e: any) =>
    String(e?.set?.id ?? e?.set_id ?? e?.setId ?? ''),
  );
  // ---------- Collector resolution (mirror FrequencyTableRow) ----------
  const getCreatedTs = (e: any) => {
    const t = e?.created_at ?? e?.createdAt ?? 0;
    if (typeof t === 'number') {
      return t;
    }
    const n = Number(t);
    if (!isNaN(n)) {
      return n;
    }
    const d = new Date(t);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  };

  const firstEventForSet = (arr: any[]) =>
    (arr || []).slice().sort((a, b) => getCreatedTs(a) - getCreatedTs(b))[0];

  const collectorIdBySet: Record<string, string | null> = {};
  const collectorIds = new globalThis.Set<string>();

  for (const s of filteredSets) {
    const sid = String(s?.id ?? '');
    const setEvents = eventsBySetId[sid] || [];
    const first = firstEventForSet(setEvents);

    let candidate: string | null = null;

    if (!first) {
      // No events => use set.createdBy / set.created_by (or *_id) if present
      candidate =
        s?.createdBy ||
        s?.created_by ||
        s?.created_by_id ||
        s?.createdById ||
        null;
    } else {
      // First event creator unless 'System' or empty
      const cb = first?.createdBy ?? first?.created_by ?? null;
      if (cb && cb !== 'System' && cb !== '') {
        candidate = String(cb);
      }
    }

    collectorIdBySet[sid] = candidate ? String(candidate) : null;
    if (candidate) {
      collectorIds.add(String(candidate));
    }
  }

  // Batch-fetch users
  const usersById: Record<string, any> = {};
  if (collectorIds.size > 0) {
    for (const chunk of _.chunk(Array.from(collectorIds), 500)) {
      const us = await database
        .get('users')
        .query(Q.where('id', Q.oneOf(chunk)))
        .fetch();
      for (const u of us) {
        usersById[u.id] = u;
      }
    }
  }

  const getCollectorNameForSet = (s: any): string => {
    const sid = String(s?.id ?? '');
    const uid = collectorIdBySet[sid];

    if (uid && usersById[uid]) {
      const u = usersById[uid];
      const full = [u?.firstName, u?.lastName].filter(Boolean).join(' ').trim();
      if (full) {
        return full;
      }
    }

    // Fallback to event-provided names if HTTP payload included them
    const evs = eventsBySetId[sid] || [];
    const first = firstEventForSet(evs);
    const fallback =
      first?.createdByName ||
      first?.created_by_name ||
      (first?.created_by
        ? [first?.created_by?.firstName, first?.created_by?.lastName]
            .filter(Boolean)
            .join(' ')
            .trim()
        : null);

    return fallback || '-';
  };
  // --------------------------------------------------------------------

  // ----- Prefetch & classify prompts (Antecedents / Consequences) -----
  const collectPromptIds = (): string[] => {
    const ids = new globalThis.Set<string>();
    for (const s of filteredSets) {
      const sid = String(s?.id ?? '');
      const setEvents = eventsBySetId[sid] || [];
      for (const e of setEvents) {
        const ps = normalizePromptsArray(e?.prompts);
        for (const p of ps) {
          if (typeof p === 'string') {
            ids.add(p);
          } else if (p?.id) {
            ids.add(p.id);
          }
        }
      }
    }
    return Array.from(ids);
  };

  const promptMetaById: Record<
    string,
    {name?: string; acronym?: string; promptType?: string}
  > = {};
  const allPromptIds = collectPromptIds();

  const fetchByIdBatches = async (
    table: string,
    ids: string[],
    idField: string = 'id',
  ) => {
    const out: any[] = [];
    for (const ch of _.chunk(ids, 500)) {
      const _rows = await database
        .get(table)
        .query(Q.where(idField, Q.oneOf(ch)))
        .fetch();
      out.push(..._rows);
    }
    return out;
  };

  if (allPromptIds.length > 0) {
    const base = await fetchByIdBatches('prompts', allPromptIds, 'id');
    for (const r of base) {
      promptMetaById[r.id] = {
        name: r?.name,
        acronym: r?.acronym,
        promptType: r?.promptType || r?.type || r?.category,
      };
    }

    const missing = allPromptIds.filter(id => !promptMetaById[id]);
    if (missing.length > 0) {
      const pp = await fetchByIdBatches('program_prompt', missing, 'id');
      for (const p of pp) {
        promptMetaById[p.id] = {
          name: p?.name,
          acronym: p?.acronym,
          promptType: p?.promptType || p?.type || p?.category,
        };
      }
      const stillMissing = missing.filter(id => !promptMetaById[id]);
      if (stillMissing.length > 0 && pp.length > 0) {
        const baseIds = _.uniq(
          pp.map((x: any) => x?.prompt_id || x?.promptId).filter(Boolean),
        );
        if (baseIds.length > 0) {
          const basePrompts = await fetchByIdBatches('prompts', baseIds, 'id');
          const baseMap: Record<
            string,
            {name?: string; acronym?: string; promptType?: string}
          > = {};
          for (const b of basePrompts) {
            baseMap[b.id] = {
              name: b?.name,
              acronym: b?.acronym,
              promptType: b?.promptType || b?.type || b?.category,
            };
          }
          for (const p of pp) {
            const m = baseMap[p?.prompt_id || p?.promptId];
            if (m && !promptMetaById[p.id]) {
              promptMetaById[p.id] = m;
            }
          }
        }
      }
    }
  }

  const classifyPrompts = (evt: any) => {
    const ps = normalizePromptsArray(evt?.prompts);
    const antecedents: string[] = [];
    const consequences: string[] = [];

    for (const p of ps) {
      let name: string | undefined;
      let acronym: string | undefined;
      let type: string | undefined;

      if (typeof p === 'string') {
        const meta = promptMetaById[p];
        if (meta) {
          name = meta.name;
          acronym = meta.acronym;
          type = meta.promptType;
        }
      } else if (p && typeof p === 'object') {
        const id = p.id;
        name = p.name ?? (id ? promptMetaById[id]?.name : undefined);
        acronym = p.acronym ?? (id ? promptMetaById[id]?.acronym : undefined);
        type =
          p.promptType ||
          p.type ||
          p.category ||
          (id ? promptMetaById[id]?.promptType : undefined);
      }

      const label = (name || acronym || '').replaceAll('"', "'");
      if (!label) {
        continue;
      }

      const t = String(type || '').toLowerCase();
      if (t.startsWith('antecedent')) {
        antecedents.push(label);
      } else if (t.startsWith('consequence')) {
        consequences.push(label);
      }
    }

    return {
      antecedents: antecedents.length ? Array.from(new Set(antecedents)) : [],
      consequences: consequences.length
        ? Array.from(new Set(consequences))
        : [],
    };
  };

  // ---------------- Build CSV rows ----------------
  for (const s of filteredSets) {
    const sid = String(s?.id ?? '');
    const setEvents = eventsBySetId[sid] || [];

    const dateStr = session
      ? moment(session?.sessionDate).format('ll')
      : moment(s?.session_date).format('ll');

    const startTs = setStart(s);
    const endTs = setEnd(s);
    const timeStamp =
      `${startTs ? moment(startTs).format('hh:mm A') : ''} - ` +
      `${endTs ? moment(endTs).format('hh:mm A') : 'Incomplete'}`;

    const collector = getCollectorNameForSet(s);

    const intensity = {mild: 0, moderate: 0, severe: 0};
    const antecedentsArr: string[] = [];
    const consequencesArr: string[] = [];

    for (const ev of setEvents) {
      const key = String(ev?.intensity || '').toLowerCase();
      if (key === 'mild' || key === 'moderate' || key === 'severe') {
        (intensity as any)[key] = ((intensity as any)[key] || 0) + 1;
      }
      const {antecedents, consequences} = classifyPrompts(ev);
      antecedentsArr.push(...antecedents);
      consequencesArr.push(...consequences);
    }

    const antecedentsStr = antecedentsArr.length
      ? Array.from(new Set(antecedentsArr)).join(', ')
      : '-';
    const consequencesStr = consequencesArr.length
      ? Array.from(new Set(consequencesArr)).join(', ')
      : '-';

    rows.push([
      dateStr,
      timeStamp,
      collector,
      `${setEvents.length}`,
      antecedentsStr,
      consequencesStr,
      `${intensity.mild}`,
      `${intensity.moderate}`,
      `${intensity.severe}`,
    ]);
  }

  return rows;
};
